import { toast } from 'react-toastify'
import { getMPDataWithoutParams } from 'src/mentor/services'

/**
 * Search options via query
 * @param {string} queryName - e.g., "therapies.search", "assessments.search"
 * @param {string} searchText - User's search input
 * @param {object} params - Additional query params from field.optionSource.params
 * @param {number} pageSize - Default 50
 * @returns {Promise<Array>} Array of {value: code, label: display} for react-select
 */
export async function fetchOptions(queryName, searchText = '', params = {}, pageSize = 50) {
  try {
    // Build query params
    const queryParams = new URLSearchParams({
      queryName,
      q: searchText,
      page: 1,
      pageSize,
      ...params,
    })

    const response = await getMPDataWithoutParams(
      `/api/secure/clinic-practitioner/emr-section-data/options/search?${queryParams.toString()}`,
    )

    if (response?.status === 200) {
      // Try different response structures
      let items = null

      // Check if items is directly in response.data
      if (response?.data?.items) {
        items = response.data.items
      }
      // Check if items is nested in response.data.data
      else if (response?.data?.data?.items) {
        items = response.data.data.items
      }

      if (items) {
        // Transform API response to react-select format
        return (items || []).map((item) => ({
          value: item.code, // Entity ID/UUID
          label: item.label, // Display name
        }))
      }
    }

    console.error('[emrOptionsApi] Invalid response format:', response)
    return []
  } catch (error) {
    console.error('[emrOptionsApi] Error fetching options:', error)
    toast.error('Failed to load options')
    return []
  }
}

/**
 * Lookup options by their codes (for loading initial selected values)
 * @param {string} queryName - e.g., "therapies.search"
 * @param {Array<string>} codes - Entity IDs to lookup
 * @param {object} params - Additional query params
 * @returns {Promise<Array>} Array of {value: code, label: display}
 */
export async function lookupOptionsByCodes(queryName, codes = [], params = {}) {
  if (!codes || codes.length === 0) {
    return []
  }

  try {
    // Build query params
    const queryParams = new URLSearchParams({
      queryName,
      codes: codes.join(','),
      ...params,
    })

    const response = await getMPDataWithoutParams(
      `/api/secure/clinic-practitioner/emr-section-data/options/lookupByCodes?${queryParams.toString()}`,
    )

    if (response?.status === 200) {
      // Try different response structures
      let items = null

      // Check if items is directly in response.data
      if (response?.data?.items) {
        items = response.data.items
      }
      // Check if items is nested in response.data.data
      else if (response?.data?.data?.items) {
        items = response.data.data.items
      }

      if (items) {
        // Transform API response to react-select format
        return (items || []).map((item) => ({
          value: item.code, // Entity ID/UUID
          label: item.label, // Display name
        }))
      }
    }

    console.error('[emrOptionsApi] Invalid response format:', response)
    return []
  } catch (error) {
    console.error('[emrOptionsApi] Error looking up options by codes:', error)
    toast.error('Failed to load selected options')
    return []
  }
}

/**
 * Debounce utility function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}
